/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/*
 * The SharedWorkers running the code for diffServiceWorker.ts broadcast their
 * availability to the host page via a BroadcastChannel. This file codifies the
 * protocol for the channel.
 */

export const AVAILABILITY_METHOD = 'diff-service-availability';

/** Union type with one variant, as only one message type is currently used. */
export type BroadcastMessage = {
  method: typeof AVAILABILITY_METHOD;
  workerName: string;
  available: boolean;
};

/**
 * Use this to create the BroadcastChannel to ensure the name is specified
 * consistently on both ends of the channel.
 */
export function createDiffServiceBroadcastChannel(): BroadcastChannel {
  return new BroadcastChannel('diff-service');
}

/**
 * Session ID unique to this page load. Ensures each page/tab gets a fresh
 * SharedWorker rather than connecting to a stale worker from a previous
 * session that may have broken state (stuck counters, closed by logout, etc.).
 * SharedWorkers are keyed by (URL, name) — a unique name forces a new instance.
 */
const SESSION_ID = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** When creating new SharedWorkers, the index should start at 0. */
export function createWorkerName(index: number) {
  return `diff-service-worker-${index}-${SESSION_ID}`;
}

export function parseWorkerIndex(workerName: string): number | null {
  const match = workerName.match(/^diff-service-worker-(\d+)/);
  return match != null ? parseInt(match[1], 10) : null;
}
